<template>
  <button
    :class="[
      'base-button',
      `base-button--${variant || 'primary'}`,
      `base-button--${size || 'md'}`,
      { 'base-button--loading': loading, 'base-button--disabled': disabled || loading }
    ]"
    :disabled="disabled || loading"
    :type="type || 'button'"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="base-button__spinner"></span>
    <slot v-else></slot>
  </button>
</template>

<script setup lang="ts">
defineProps<{
  variant?: 'primary' | 'ghost' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}>();

defineEmits<{
  click: [event: MouseEvent];
}>();
</script>

<style scoped>
.base-button {
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  outline: none;
}

/* Sizes */
.base-button--sm {
  height: 32px;
  padding: 0 10px;
  font-size: 0.78rem;
  border-radius: var(--radius-sm);
}

.base-button--md {
  height: 38px;
  padding: 0 14px;
  font-size: 0.875rem;
}

.base-button--lg {
  height: 44px;
  padding: 0 20px;
  font-size: 0.925rem;
}

/* Primary */
.base-button--primary {
  background: var(--color-primary);
  color: #ffffff;
  border: 1.5px solid transparent;
}

.base-button--primary:hover:not(:disabled) {
  background: var(--color-primary-dark);
  box-shadow: 0 2px 8px rgba(30, 86, 217, 0.3);
}

.base-button--primary:focus-visible {
  box-shadow: 0 0 0 3px rgba(30, 86, 217, 0.25);
}

/* Ghost */
.base-button--ghost {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1.5px solid var(--color-border-medium);
}

.base-button--ghost:hover:not(:disabled) {
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border-color: var(--color-border-medium);
}

.base-button--ghost:focus-visible {
  box-shadow: 0 0 0 3px rgba(30, 86, 217, 0.12);
}

/* Secondary */
.base-button--secondary {
  background: var(--color-primary-light);
  color: var(--color-primary);
  border: 1.5px solid transparent;
}

.base-button--secondary:hover:not(:disabled) {
  background: #d4e4fc;
}

.base-button--secondary:focus-visible {
  box-shadow: 0 0 0 3px rgba(30, 86, 217, 0.18);
}

/* Danger */
.base-button--danger {
  background: var(--color-error);
  color: #ffffff;
  border: 1.5px solid transparent;
}

.base-button--danger:hover:not(:disabled) {
  background: var(--color-error-dark);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.base-button--danger:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
}

/* Disabled */
.base-button--disabled,
.base-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Loading */
.base-button--loading {
  cursor: wait;
  pointer-events: none;
}

.base-button__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-right-color: currentColor;
  border-radius: 50%;
  animation: spin 0.65s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
